import React, { useState } from 'react';
import * as api from '../../api';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import dayjs from 'dayjs';
import Spinner from '../../Plateforme/Atoms/Spinner';
import Section from '../Atoms/Section';
import H0 from '../Atoms/H0';
import H2 from '../Atoms/H2';
import BlueButton from '../Atoms/BlueButton';
import Input from '../Atoms/Input';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import ReactSelect from 'react-select';
import { FD100MultiSelectOptions } from '../Utils/Listings';
import ProjectDetailView from '../Components/ProjectDetailView';
import BigH3 from '../Atoms/BigH3';
import GetProjectMainImage from '../Utils/GetProjectMainImage';
import { createUrlWithRedirect } from '../Utils/redirectUtils';
import { getUserRegistrations } from '../Components/SDE24/sdeSupabaseService';
import { PROFIL_CREATEUR_OPTIONS } from '../Components/SDE24/sdeConstants';

const CandidatureHeader = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    
    & .event-logo {
        width: 80px;
        height: 80px;
        object-fit: contain;
        margin-right: 20px;
        background-color: #f5f5f5;
        padding: 5px;
        border-radius: 5px;
    }
    
    & .title-badge {
        flex: 1;
    }
`;

const CandidatureContainer = styled.div`
    background-color: white;
    margin: 20px 0;
    padding: 30px;
    border-radius: 0px;
    border:solid 1px black;
    position: relative;
    
    & .date-fin-inscription {
        position: absolute;
        top: 10px;
        right: 10px;
        padding: 5px 10px;
        border-radius: 5px;
        font-weight: bold;
        font-size: 14px;
    }
`;

const CandidatureContentLayout = styled.div`
    display: flex;
    margin-top: 20px;
    
    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const ContactInfoColumn = styled.div`
    flex: 1;
    padding-right: 30px;
    border-right: 1px solid black;
    
    @media (max-width: 768px) {
        padding-right: 0;
        padding-bottom: 20px;
        border-right: none;
        border-bottom: 1px solid #eee;
    }
`;

const ProjectsColumn = styled.div`
    flex: 2;
    padding-left: 30px;
    
    @media (max-width: 768px) {
        padding-left: 0;
        padding-top: 20px;
    }
`;

const ProjectsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    margin-top: 15px;
    
    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const ProjectCard = styled.div`
    display: flex;
    flex-direction: column;
    background-color: rgb(252, 252, 252);
    border-radius: 0px;
    border: solid 1px black;
    overflow: hidden;
    width: 200px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    cursor: pointer;
    position: relative;

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 4px 8px rgba(0,0,0,0.15);
    }

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const AddProjectCard = styled(ProjectCard)`
    justify-content: center;
    align-items: center;
    height: 252px; /* Match the height of project cards */
    background-color: rgb(252, 252, 252);
    border: 2px dashed black;
    
    & .plus-icon {
        font-size: 40px;
        color: #0000FF;
        margin-bottom: 10px;
    }
    
    & .plus-text {
        color: #0000FF;
        font-weight: bold;
    }
`;

const ProjectTitle = styled.div`
    font-weight: bold;
    padding: 12px;
    /* background-color: #e6f0ff; */
`;

const ProjectImage = styled.div`
    height: 150px;
    background-color: #ddd;
    background-image: ${props => props.src ? `url(${props.src})` : 'none'};
    background-size: cover;
    background-position: center;
    position: relative;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.1);
        opacity: ${props => props.src ? 0 : 1};
    }
`;

const ProjectInfo = styled.div`
    padding: 12px;
    font-size: 13px;
    color: #666;
    
    & > div {
        margin-bottom: 5px;
    }
`;

const DetailItem = styled.div`
    margin-bottom: 15px;
    
    & .label {
        font-weight: bold;
        color: rgb(0, 0, 255);
        margin-bottom: 5px;
        display: block;
    }
    
    & .value {
        color: #333;
        font-weight: bold;
    }
`;

const EventBadge = styled.div`
    background-color: blue;
    color: white;
    display: inline-block;
    padding: 5px 10px;
    border-radius: 5px;
    margin-bottom: 15px;
`;

const EditableContactInfo = styled.div`
    margin-bottom: 15px;
    
    & .label {
        font-weight: bold;
        color: rgb(0, 0, 255);
        margin-bottom: 5px;
        display: block;
    }
    
    & .value {
        color: #333;
    }
    
    & .edit-buttons {
        display: flex;
        gap: 10px;
        margin-top: 15px;
    }
`;

const SaveButton = styled.button`
    background-color: #4caf50;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-weight: bold;
    
    &:hover {
        background-color: #45a049;
    }
`;

const CancelButton = styled.button`
    background-color: #f44336;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-weight: bold;

    &:hover {
        background-color: #d32f2f;
    }
`;

const MilanSubmissionContainer = styled(CandidatureContainer)`
    background-color: #fafafa;
    border: 2px solid #0000FF;
`;

const MilanPieceCard = styled.div`
    background: white;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
    border: 1px solid #e0e0e0;
`;

const MilanPieceTitle = styled.h4`
    color: #0000FF;
    margin-bottom: 15px;
    font-size: 18px;
`;

const MilanDataGrid = styled.div`
    display: grid;
    grid-template-columns: 200px 1fr;
    gap: 10px;
    margin-bottom: 10px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 5px;
    }
`;

const MilanDataLabel = styled.div`
    font-weight: 600;
    color: #555;
    font-size: 14px;
`;

const MilanDataValue = styled.div`
    color: #333;
    white-space: pre-wrap;
    font-size: 14px;
`;

const MilanMediaSection = styled.div`
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #e0e0e0;
`;

const MilanMediaGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 15px;
    margin-top: 15px;
`;

const MilanMediaCard = styled.div`
    background: white;
    border: 1px solid #ddd;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        transform: translateY(-2px);
    }
`;

const MilanMediaPreview = styled.div`
    width: 100%;
    height: 120px;
    background: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const MilanMediaInfo = styled.div`
    padding: 8px;
    font-size: 11px;

    .filename {
        font-weight: 500;
        color: #333;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const WinnerBadge = styled.div`
    background: linear-gradient(135deg, #FFD700, #FFA500);
    color: black;
    padding: 6px 12px;
    border-radius: 5px;
    font-weight: bold;
    font-size: 12px;
    margin: 8px;
    display: inline-block;
    box-shadow: 0 2px 8px rgba(255, 215, 0, 0.3);
    cursor: pointer;
    transition: transform 0.2s ease;
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;

    &:hover {
        transform: scale(1.05);
    }
`;

// List of winner project IDs from FD100 2025
const WINNER_PROJECT_IDS = [
    '67a332e21b103d938bafb0dc',
    '67a0b8331b103d938bafa954',
    '67aa1011740d844a3d633e93',
    '6829daa5754f51df2ef9141c',
    '67e6c18043d5609350e381a8',
    '67b5e6146890f1a147192905',
    '67be4a58b1ba28c3144aeb73',
    '67bf2330b1ba28c3144af8d2',
    '67c090dcdb48cb16a13c5b43',
    '677268097ec5d90c89d6fe63',
    '67c6b416db48cb16a13c73c7',
    '67ab652c07736060460b3b75',
    '68348986754f51df2ef9b87c',
    '67d15108d95ccf24eaf191df',
    '67d7d38c43d5609350e242a1',
    '67d82d2243d5609350e24c5e',
    '662adc507159e5b1b1da7c75',
    '67d8718c43d5609350e25c38',
    '67d1977243d5609350e218f2',
    '682b5c5f754f51df2ef93bbc',
    '67da910a43d5609350e274d7',
    '67e1864b43d5609350e2baeb',
    '67daebf343d5609350e27a9e',
    '67dd585043d5609350e28e57',
    '67dfeb0e43d5609350e2a360',
    '67e1452a43d5609350e2b066',
    '678675e2de3ed4a7001ab532',
    '67e2d05943d5609350e2cf97',
    '67e3e1cb43d5609350e304e5',
    '67e3e81543d5609350e308de',
    '67e3f02643d5609350e30a0a',
    '67e42d7043d5609350e32539',
    '67e42e5543d5609350e32570',
    '67e45c9843d5609350e329c0',
    '67e529f743d5609350e33a8c',
    '67e53aab43d5609350e340c3',
    '67e56ab043d5609350e3490f',
    '67e5764d43d5609350e34cb5',
    '67e57f5143d5609350e3519a',
    '67e5864c43d5609350e353a3',
    '67e58da443d5609350e3561d',
    '67e58ebc43d5609350e35665',
    '67e5ab6b43d5609350e35905',
    '67e6828d43d5609350e36b6a',
    '67e6acaf43d5609350e37832',
    '67e51ddb43d5609350e33667',
    '67e6c0a843d5609350e380cf',
    '67e6d53a43d5609350e38725',
    '67e6dfff43d5609350e38900',
    '67e7003c43d5609350e38bfc',
    '6834625c754f51df2ef9a4b1',
    '67ea99b76b7945ed1a576bae',
    '67f90250a0262e085d37088f',
    '67e1741a43d5609350e2b608',
    '67fe7a6c9167c049fff4e37e',
    '67ab6ade07736060460b3c16',
    '6810a343264e7745556acae1',
    '6800f4c59167c049fff51f5d',
    '6800b8529167c049fff50e47',
    '6800e76e9167c049fff5197f',
    '6800ef849167c049fff51c6f',
    '6800fad59167c049fff52224',
    '680113739167c049fff52d73',
    '680106139167c049fff525af',
    '680125219167c049fff53405',
    '680957ab9167c049fff5a7c2',
    '681164d36c8fbfe5e3c91e9e',
    '68020ef79167c049fff54015',
    '68025d069167c049fff56132',
    '680b728c9167c049fff5c8f7',
    '680249249167c049fff55a19',
    '68024ed89167c049fff55b90',
    '68025b1a9167c049fff56037',
    '680270379167c049fff566f5',
    '680274c09167c049fff567c8',
    '680275419167c049fff567de',
    '68027b389167c049fff56954',
    '680291609167c049fff56cad',
    '6802a3fc9167c049fff56f5f',
    '6849787b17dde4ca4c4bf043',
    '6849435a17dde4ca4c4be251',
    '6807cb759167c049fff58dd4',
    '6808c3f59167c049fff599c7',
    '6808c3859167c049fff599b1',
    '680fa4b19167c049fff5deee',
    '6801e2f49167c049fff53ce1',
    '680a54a09167c049fff5bbc4',
    '680b94b29167c049fff5cd49',
    '6810df4a264e7745556ad75d',
    '680fa3f89167c049fff5deb1',
    '680fa844a71ddf126b77b977',
    '680fa9f09167c049fff5e027',
    '6810c9b9264e7745556acf4d',
    '681125b66c8fbfe5e3c91c62',
    '6818cac19e656d77bc6c72fa',
    '6818d27e9e656d77bc6c75a3',
    '6818dbdf9e656d77bc6c78a0',
    '682d97b2754f51df2ef96070',
    '67fccffaa0262e085d370ddc',
    '680271809167c049fff5674a'
];

// Function to convert value to options for ReactSelect
function valuesToOptions(value, options) {
    // Make sure value is an array
    if (!Array.isArray(value)) {
        value = [value];
    }
    
    const optionsFlattened = [];
    for (let i = 0; i < options.length; i++) {
        if (options[i].options) {
            optionsFlattened.push(...options[i].options);
        }
        if (options[i].value) {
            optionsFlattened.push(options[i]);
        }
    }
    
    return optionsFlattened.filter(opt => value.includes(opt.value));
}

const MyCandidatures = (props) => {
    const { i18n } = useTranslation('common');
    const [candidatures, setCandidatures] = useState([]);
    const [milanSubmissions, setMilanSubmissions] = useState([]);
    const [sdoRegistrations, setSdoRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingCandidature, setEditingCandidature] = useState(null);
    const [formData, setFormData] = useState({});
    const [selectedProject, setSelectedProject] = useState(null);
    const [currentCandidature, setCurrentCandidature] = useState(null);
    const [creatingProject, setCreatingProject] = useState(false);
    const navigate = useNavigate();
    const partageCultureFrancaiseFD100 = FD100MultiSelectOptions.partageCultureFrancaiseFD100(i18n.language);
    const { typologieObjetFD100, tyoplogieEspaceFD100 } = FD100MultiSelectOptions;

    const getTypologyLabel = (value) => {
        if (!value) return '';

        // Check in object typologies
        const objetType = typologieObjetFD100(i18n.language).find(t => t.value === value);
        if (objetType) return objetType.label;

        // Check in space typologies (note: typo in original function name)
        const espaceType = tyoplogieEspaceFD100(i18n.language).find(t => t.value === value);
        if (espaceType) return espaceType.label;

        return value;
    };

    const location = useLocation();

    React.useEffect(() => {
        if (props.fullUserLoading) return;
        if (!props.fullUser?.user) {
            navigate(createUrlWithRedirect('/login', location.pathname));
            return;
        }
        
        fetchCandidatures();
        fetchMilanSubmissions();
        fetchSdoRegistrations();
    }, [props.fullUserLoading, props.fullUser, navigate]);

    const fetchMilanSubmissions = async () => {
        if (!props.fullUser?.user?._id) return;

        try {
            const response = await api.getUserFormSubmissions(props.fullUser.user._id);
            setMilanSubmissions(response.data.submissions || []);
        } catch (error) {
            console.error("Error fetching Milan 2026 submissions:", error);
        }
    };

    const fetchSdoRegistrations = async () => {
        const email = props.fullUser?.hsProfile?.email || props.fullUser?.user?.email;
        if (!email) return;
        try {
            const res = await getUserRegistrations(email);
            setSdoRegistrations(res.data || []);
        } catch (error) {
            console.error("Error fetching SDO registrations:", error);
        }
    };

    const fetchCandidatures = async () => {
        try {
            setLoading(true);
            
            // Get events to find FD100 events
            const eventsResponse = await api.getEvents();
            
            // Filter for FD100 events
            const fd100Events = eventsResponse.data.filter(event => event.type === "fd100" && event._id == "67590da5499bcc995c210b40");//limit to fd100 2025

            
            // For each FD100 event, check if the user has a participation
            const fd100Participations = [];
            
            // Get user participations once to avoid multiple API calls
            const userParticipations = await api.getUserParticipations({ userid: props.fullUser.user._id });
            
            for (const event of fd100Events) {
                try {
                    // Find the participation for this specific event
                    const eventParticipation = userParticipations.data.find(p => p.eventId === event._id);
                    
                    if (eventParticipation) {
                        // For each project in the participation, retrieve the full project data
                        let enrichedProjects = [];
                        if (eventParticipation.data && eventParticipation.data.projets && props.fullUser.company && props.fullUser.company.projects) {
                            // This maps the project IDs to the full project objects from the company
                            enrichedProjects = eventParticipation.data.projets
                                .map(projectId => {
                                    // First check if projectId is an object (it might already be the full project)
                                    if (typeof projectId === 'object' && projectId !== null) {
                                        return projectId;
                                    }
                                    
                                    // Otherwise look for the project in the company projects
                                    const projectFromCompany = props.fullUser.company.projects.find(p => p._id === projectId);
                                    
                                    return projectFromCompany || { 
                                        id: projectId, 
                                        name: "Project " + (typeof projectId === 'string' ? projectId.substring(0, 5) : 'Unknown'),
                                        medias: [] // Empty medias array to avoid errors
                                    };
                                })
                                .filter(Boolean); // Remove any undefined entries
                        }
                        
                        fd100Participations.push({
                            ...eventParticipation,
                            event: event,
                            eventName: event.title_fr || event.name,
                            eventType: event.type,
                            // Include the enriched projects in the data
                            data: {
                                ...eventParticipation.data,
                                projets: enrichedProjects.length > 0 ? enrichedProjects : (eventParticipation.data?.projets || [])
                            }
                        });
                    }
                } catch (error) {
                    console.error(`Error processing participation for event ${event._id}:`, error);
                }
            }
            
            setCandidatures(fd100Participations);
        } catch (error) {
            console.error("Error fetching candidatures:", error);
        } finally {
            setLoading(false);
        }
    };

    const formatDateIfExists = (dateString) => {
        if (!dateString) return '';
        return dayjs(dateString).format('DD/MM/YYYY');
    };

    const handleEdit = (candidature) => {
        setEditingCandidature(candidature);
        setFormData({
            contact_nom: candidature.data?.contact_nom || '',
            contact_prenom: candidature.data?.contact_prenom || '',
            contact_mail: candidature.data?.contact_mail || '',
            contact_telephone: candidature.data?.contact_telephone || '',
            culture_francaise: candidature.data?.culture_francaise || ''
        });
    };

    const handleCancel = () => {
        setEditingCandidature(null);
        setFormData({});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSelectChange = (selectedOption) => {
        setFormData({
            ...formData,
            culture_francaise: selectedOption.value
        });
    };

    const handleSave = async (candidatureId) => {
        try {
          setLoading(true); // Add loading state
          
          // Find the candidature being edited
          const candidature = candidatures.find(c => c._id === candidatureId);
          if (!candidature) return;
      
          api.LogFront(`FD1002025 updating contact info with ID: ${candidatureId}`);
      
          // Create the FormData object for submission
          const submission = new FormData();
          
          // Essential fields
          submission.append("userId", props.fullUser.user._id);
          submission.append("eventId", candidature.eventId);
          submission.append("participationId", candidatureId);
          
          // Add the contact info fields
          submission.append("culture_francaise", formData.culture_francaise || '');
          submission.append("contact_nom", formData.contact_nom || '');
          submission.append("contact_prenom", formData.contact_prenom || '');
          submission.append("contact_mail", formData.contact_mail || '');
          submission.append("contact_telephone", formData.contact_telephone || '');
          
          // Preserve existing projects by properly converting them to JSON
          if (candidature.data && candidature.data.projets) {
            // Make sure to use a clean version of projects without invalid file references
            const cleanProjects = candidature.data.projets.map(project => {
              // Create a clean copy without problematic properties
              const { newMedias, ...cleanProject } = project;
              return cleanProject;
            });
            
            submission.append("projets", JSON.stringify(cleanProjects));
          }
      
          // Make the API call
          const response = await api.saveFD1002025(submission);
          
          if (response.data === "success") {
            // Update local state
            const updatedCandidatures = candidatures.map(c => {
              if (c._id === candidatureId) {
                return {
                  ...c,
                  data: {
                    ...c.data,
                    culture_francaise: formData.culture_francaise || c.data.culture_francaise,
                    contact_nom: formData.contact_nom || c.data.contact_nom,
                    contact_prenom: formData.contact_prenom || c.data.contact_prenom,
                    contact_mail: formData.contact_mail || c.data.contact_mail,
                    contact_telephone: formData.contact_telephone || c.data.contact_telephone
                  }
                };
              }
              return c;
            });

            props.refreshUser();
            
            setCandidatures(updatedCandidatures);
            setEditingCandidature(null);
            setFormData({});
            
            // Show success message
            alert(i18n.language === 'fr' ? 'Informations mises à jour avec succès!' : 'Information updated successfully!');
          } else {
            throw new Error(response.data || 'Unknown error');
          }
        } catch (error) {
          console.error("Error updating contact info:", error);
          alert(i18n.language === 'fr' 
            ? 'Erreur lors de la mise à jour. Veuillez réessayer.' 
            : 'Error updating information. Please try again.');
        } finally {
          setLoading(false); // Always turn off loading state
        }
    };
    
    const handleProjectClick = (project, candidature) => {
        setSelectedProject(project);
        setCurrentCandidature(candidature);
        setCreatingProject(false);
    };
    
    const handleCreateProject = (candidature) => {
        setCurrentCandidature(candidature);
        setSelectedProject(null);
        setCreatingProject(true);
    };

    const handleProjectUpdate = async (updatedProject) => {
        console.log("Project update triggered with:", updatedProject);
      
        // Set loading state to show loader
        setLoading(true);
        
        try {
          // Perform a complete refresh of candidature data
          const eventsResponse = await api.getEvents();
          const fd100Events = eventsResponse.data.filter(event => event.type === "fd100" && event._id == "67590da5499bcc995c210b40");//limit to fd100 2025
          const userParticipations = await api.getUserParticipations({ userid: props.fullUser.user._id });
          
          console.log("Refreshed user participations:", userParticipations);
          
          // Process and update the candidatures data
          const refreshedFd100Participations = [];
          
          for (const event of fd100Events) {
            try {
              const eventParticipation = userParticipations.data.find(p => p.eventId === event._id);
              
              if (eventParticipation) {
                // Process project data to ensure all projects have complete information
                let enrichedProjects = [];
                
                if (eventParticipation.data && eventParticipation.data.projets) {
                  enrichedProjects = eventParticipation.data.projets.map(projectId => {
                    // Check if projectId is already an object
                    if (typeof projectId === 'object' && projectId !== null) {
                      // If this is the updated project, use the updated version
                      if (projectId.id_in_participation === updatedProject.id_in_participation) {
                        console.log("Found matching project to update in state:", projectId.id_in_participation);
                        return updatedProject;
                      }
                      return projectId;
                    }
                    
                    // If projectId is a string, we need to find the full project data
                    // First check if it matches our updated project's company ID
                    if (projectId === updatedProject.id_in_company) {
                      console.log("Found matching project by company ID:", projectId);
                      return updatedProject;
                    }
                    
                    // Otherwise look in the company projects
                    const projectFromCompany = props.fullUser.company.projects.find(p => p._id === projectId);
                    
                    return projectFromCompany || { 
                      id: projectId, 
                      name: "Project " + (typeof projectId === 'string' ? projectId.substring(0, 5) : 'Unknown'),
                      medias: [] 
                    };
                  }).filter(Boolean);
                }
                
                refreshedFd100Participations.push({
                  ...eventParticipation,
                  event: event,
                  eventName: event.title_fr || event.name,
                  eventType: event.type,
                  data: {
                    ...eventParticipation.data,
                    projets: enrichedProjects.length > 0 ? enrichedProjects : (eventParticipation.data?.projets || [])
                  }
                });
              }
            } catch (error) {
              console.error(`Error processing participation for event ${event._id}:`, error);
            }
          }
          
          console.log("Setting updated candidatures with processed data");
          setCandidatures(refreshedFd100Participations);
        } catch (error) {
          console.error("Error refreshing candidatures data:", error);
          alert(i18n.language === 'fr' 
            ? 'Erreur lors de la mise à jour des données. Veuillez rafraîchir la page.' 
            : 'Error updating data. Please refresh the page.');
        } finally {
          setLoading(false);
        }
    };
    
    const handleBackToProjects = () => {
        setSelectedProject(null);
        setCurrentCandidature(null);
        setCreatingProject(false);
        
        // Refresh candidatures data
        fetchCandidatures();
    };

    if (loading) {
        return (
            <Section className="padding">
                <br/><br/>
                <Spinner />
            </Section>
        );
    }

    const inscriptionsOpened = (date) => {
        const dateObj = new Date(date);
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);
        return dateObj.getTime() > currentDate.getTime();
    }

    return (
        <Section className="padding">
            <div className="flexblock" style={{alignItems: 'center'}}>
                <H0>{i18n.language === 'fr' ? 'Mes candidatures' : 'My applications'}</H0>
            </div>
            
            {candidatures.length === 0 && milanSubmissions.length === 0 && sdoRegistrations.length === 0 ? (
                <div className="centered" style={{margin: '50px 0'}}>
                    <H2>{i18n.language === 'fr' ? 'Vous n\'avez pas encore de candidatures' : 'You don\'t have any applications yet'}</H2>
                    <br/>
                    <p className="thin">{i18n.language === 'fr' ? 'Participez à un événement pour créer votre première candidature' : 'Participate in an event to create your first application'}</p>
                    <br/>
                    <BlueButton onClick={() => navigate('/evenements')}>
                        <img src="/img/fleche_blanche.png" alt=""/>
                        {i18n.language === 'fr' ? 'Voir les événements' : 'View events'}
                    </BlueButton>
                </div>
            ) : (
                <>
                    {candidatures.map((candidature, index) => (
                        <CandidatureContainer key={index}>
                            <div className="date-fin-inscription">
                                {i18n.language === 'fr' ? 'Fin des inscriptions:' : 'Registration deadline:'} {formatDateIfExists(candidature.event.inscriptions_end_date)}
                            </div>

                            <CandidatureHeader>
                                {candidature.event && candidature.event.logo_path && (
                                    <img 
                                        className="event-logo" 
                                        src={`${process.env.REACT_APP_MEDIA_BASE_URL}speeddating/${candidature.event.logo_path}`} 
                                        alt="Event logo" 
                                    />
                                )}
                                <div className="title-badge">
                                    {candidature.eventName && (
                                        <EventBadge>
                                            {candidature.eventName}
                                        </EventBadge>
                                    )}
                                </div>
                            </CandidatureHeader>
                            
                            {/* Show project detail view if a project is selected for this candidature */}
                            {selectedProject && currentCandidature && currentCandidature._id === candidature._id ? (
                                <ProjectDetailView 
                                    project={selectedProject}
                                    candidature={candidature}
                                    fullUser={props.fullUser}
                                    onBack={handleBackToProjects}
                                    onUpdate={handleProjectUpdate}
                                />
                            ) : creatingProject && currentCandidature && currentCandidature._id === candidature._id ? (
                                <ProjectDetailView 
                                    project={null}
                                    candidature={candidature}
                                    fullUser={props.fullUser}
                                    onBack={handleBackToProjects}
                                    onUpdate={handleProjectUpdate}
                                    isNewProject={true}
                                />
                            ) : (
                                <CandidatureContentLayout>
                                    <ContactInfoColumn>
                                        <BigH3 style={{marginBottom:"15px"}}>{i18n.language === 'fr' ? 'Informations de contact' : 'Contact Information'}</BigH3>
                                        
                                        {editingCandidature && editingCandidature._id === candidature._id ? (


                                            // Editing mode form
                                            <EditableContactInfo>
                                                <i>Coordonnées de la personne à contacter dans le cadre de cette candidature</i>

                                              
                                                
                                                <div className="label" style={{marginTop: "20px"}}>{i18n.language === 'fr' ? 'Nom:' : 'Last name:'}</div>
                                                <Input 
                                                    name="contact_nom" 
                                                    value={formData.contact_nom || ''} 
                                                    onChange={handleChange} 
                                                    style={{width: "100%"}}
                                                />
                                                
                                                <div className="label">{i18n.language === 'fr' ? 'Prénom:' : 'First name:'}</div>
                                                <Input 
                                                    name="contact_prenom" 
                                                    value={formData.contact_prenom || ''} 
                                                    onChange={handleChange} 
                                                    style={{width: "100%"}}
                                                />
                                                
                                                <div className="label">{i18n.language === 'fr' ? 'Email:' : 'Email:'}</div>
                                                <Input 
                                                    name="contact_mail" 
                                                    value={formData.contact_mail || ''} 
                                                    onChange={handleChange} 
                                                    style={{width: "100%"}}
                                                />
                                                
                                                <div className="label">{i18n.language === 'fr' ? 'Téléphone:' : 'Phone:'}</div>
                                                <Input 
                                                    name="contact_telephone" 
                                                    value={formData.contact_telephone || ''} 
                                                    onChange={handleChange} 
                                                    style={{width: "100%"}}
                                                />

                                                  <div className="label">{i18n.language === 'fr' ? 'Le Candidat doit partager la culture française en étant :' : 'The Candidate must share the French culture by being:'}</div>
                                                <ReactSelect
                                                    name="culture_francaise"
                                                    options={partageCultureFrancaiseFD100}
                                                    defaultValue={valuesToOptions(candidature.data?.culture_francaise, partageCultureFrancaiseFD100)}
                                                    onChange={handleSelectChange}
                                                    placeholder={i18n.language === 'fr' ? 'Sélectionnez votre réponse' : 'Choose your answer'}
                                                    className="select-container"
                                                />
                                                
                                                <div className="edit-buttons">
                                                    <SaveButton onClick={() => handleSave(candidature._id)}>
                                                        {i18n.language === 'fr' ? 'Enregistrer' : 'Save'}
                                                    </SaveButton>
                                                    <CancelButton onClick={handleCancel}>
                                                        {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                                                    </CancelButton>
                                                </div>
                                            </EditableContactInfo>
                                        ) : (
                                            // Display mode
                                            <>
                                               
                                                
                                                {candidature.data?.contact_nom && (
                                                    <DetailItem>
                                                        <div className="label">{i18n.language === 'fr' ? 'Nom:' : 'Last name:'}</div>
                                                        <div className="value">{candidature.data.contact_nom}</div>
                                                    </DetailItem>
                                                )}
                                                
                                                {candidature.data?.contact_prenom && (
                                                    <DetailItem>
                                                        <div className="label">{i18n.language === 'fr' ? 'Prénom:' : 'First name:'}</div>
                                                        <div className="value">{candidature.data.contact_prenom}</div>
                                                    </DetailItem>
                                                )}
                                                
                                                {candidature.data?.contact_mail && (
                                                    <DetailItem>
                                                        <div className="label">{i18n.language === 'fr' ? 'Email:' : 'Email:'}</div>
                                                        <div className="value">{candidature.data.contact_mail}</div>
                                                    </DetailItem>
                                                )}
                                                
                                                {candidature.data?.contact_telephone && (
                                                    <DetailItem>
                                                        <div className="label">{i18n.language === 'fr' ? 'Téléphone:' : 'Phone:'}</div>
                                                        <div className="value">{candidature.data.contact_telephone}</div>
                                                    </DetailItem>
                                                )}
                                                 {candidature.data?.culture_francaise && (
                                                    <DetailItem>
                                                        <div className="label">{i18n.language === 'fr' ? 'Le Candidat doit partager la culture française en étant:' : 'The Candidate must share the French culture by being:'}</div>
                                                        <div className="value">{partageCultureFrancaiseFD100.find(option => option.value === candidature.data.culture_francaise)?.label || (i18n.language === 'fr' ? 'Non spécifié' : 'Not specified')}</div>
                                                    </DetailItem>
                                                )}
                                                
                                                {candidature.data?.submittedDate && (
                                                    <DetailItem>
                                                        <div className="label">{i18n.language === 'fr' ? 'Date de soumission:' : 'Submission date:'}</div>
                                                        <div className="value">{formatDateIfExists(candidature.data.submittedDate)}</div>
                                                    </DetailItem>
                                                )}
                                                
                                                {inscriptionsOpened(candidature.event.inscriptions_end_date) &&
                                                    <div style={{marginTop: '15px'}}>
                                                        <BlueButton onClick={() => handleEdit(candidature)}>
                                                            <img src="/img/fleche_blanche.png" alt=""/>
                                                            {i18n.language === 'fr' ? 'Modifier informations' : 'Edit information'}
                                                        </BlueButton>
                                                    </div>
                                                }
                                            </>
                                        )}
                                    </ContactInfoColumn>
                                    
                                    <ProjectsColumn>
                                        <BigH3 style={{marginBottom:"15px"}}>{i18n.language === 'fr' ? 'Projets' : 'Projects'}</BigH3>
                                        
                                        {candidature.data?.projets && (
                                            <ProjectsRow>
                                                {candidature.data.projets.map((project, projectIndex) => {
                                                    const isWinner = WINNER_PROJECT_IDS.includes(project._id);
                                                    return (
                                                        <ProjectCard
                                                            key={projectIndex}
                                                            onClick={() => handleProjectClick(project, candidature)}
                                                        >
                                                            {/* {isWinner && (
                                                                <WinnerBadge
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        navigate('/mywinningprojects');
                                                                    }}
                                                                    title={i18n.language === 'fr' ? 'Cliquez pour voir la page lauréat' : 'Click to view winner page'}
                                                                >
                                                                    🏆 {i18n.language === 'fr' ? 'Lauréat' : 'Winner'}
                                                                </WinnerBadge>
                                                            )} */}
                                                            <ProjectTitle>{project.name || "Projet sans nom"}</ProjectTitle>
                                                            <ProjectImage
                                                                src={GetProjectMainImage(project) || ''}
                                                            />
                                                            <ProjectInfo>
                                                                <div style={{color: 'black',fontWeight:'bold',fontSize:'1rem'}} className=''>{project.type === 'objet'
                                                                    ? (i18n.language === 'fr' ? 'Objet' : 'Object')
                                                                    : (i18n.language === 'fr' ? 'Espace' : 'Space')}
                                                                </div>
                                                                {project.typologie &&
                                                                    <div className='minilabel'>{i18n.language === 'fr' ? 'Typologie: ' : 'Typology: '}<b style={{color: 'black',fontWeight:'bold'}}> {project.typologie}</b></div>
                                                                }
                                                                {project.year &&
                                                                    <div className='minilabel'>{i18n.language === 'fr' ? 'Année: ' : 'Year: '}<b style={{color: 'black',fontWeight:'bold'}}> {project.year}</b></div>
                                                                }
                                                            </ProjectInfo>
                                                        </ProjectCard>
                                                    );
                                                })}




                                                
                                                {/* Add Project Card with plus icon */}
                                                {inscriptionsOpened(candidature.event.inscriptions_end_date) &&
                                                    <AddProjectCard onClick={() => handleCreateProject(candidature)}>
                                                        <div className="plus-icon">+</div>
                                                        <div className="plus-text">{i18n.language === 'fr' ? 'Ajouter un projet' : 'Add a project'}</div>
                                                    </AddProjectCard>
                                                }
                                                {/* Add Project from portfolio Card with plus icon */}
                                                {inscriptionsOpened(candidature.event.inscriptions_end_date) &&
                                                    <AddProjectCard onClick={() => window.location.href = `evenements/fd100editparticipation?eid=${candidature.event._id}&uid=${props.fullUser.user._id}&addprojectfromportfolio=true`}>
                                                        <div className="plus-icon">+</div>
                                                        <div className="plus-text centered">{i18n.language === 'fr' ? 'Ajouter un projet de votre portfolio' : 'Add a project from your portfolio'}</div>
                                                    </AddProjectCard>
                                                }
                                            </ProjectsRow>
                                        )}
                                    </ProjectsColumn>
                                </CandidatureContentLayout>
                            )}
                        </CandidatureContainer>
                    ))}
                </>
            )}

            {/* SDO Registrations */}
            {sdoRegistrations.length > 0 && (
                <>
                    {sdoRegistrations.map((reg) => {
                        const fr = i18n.language === 'fr';
                        const isPoplA = reg.population_type === 'pop_a';
                        return (
                            <CandidatureContainer key={reg.id} style={{ borderColor: '#2563EB' }}>
                                <CandidatureHeader>
                                    <div className="title-badge">
                                        <EventBadge style={{ background: '#2563EB' }}>
                                            {reg.event?.title_fr || 'Speed Dating Design Objet 2026'}
                                        </EventBadge>
                                        <span style={{ marginLeft: '10px', fontSize: '13px', color: '#666' }}>
                                            {isPoplA ? (fr ? 'Fabricant / Éditeur / Galeriste' : 'Manufacturer / Publisher / Gallery') : (fr ? 'Designer / Créateur' : 'Designer / Creator')}
                                        </span>
                                    </div>
                                    <div className="date-fin-inscription" style={{ position: 'static', padding: '5px 10px', borderRadius: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                                        <span style={{ color: reg.status === 'submitted' ? '#2e7d32' : reg.status === 'validated' ? '#1565c0' : '#e65100', background: reg.status === 'submitted' ? '#e8f5e9' : reg.status === 'validated' ? '#e3f2fd' : '#fff3e0', padding: '4px 10px', borderRadius: '4px' }}>
                                            {reg.status === 'submitted' ? (fr ? 'Soumise' : 'Submitted') : reg.status === 'validated' ? (fr ? 'Validée' : 'Validated') : (fr ? 'Brouillon' : 'Draft')}
                                        </span>
                                    </div>
                                </CandidatureHeader>

                                <CandidatureContentLayout>
                                    <ContactInfoColumn>
                                        <BigH3 style={{ marginBottom: '15px' }}>{fr ? 'Informations de contact' : 'Contact Information'}</BigH3>

                                        {reg.representative_1?.first_name && (
                                            <DetailItem>
                                                <div className="label">{fr ? 'Représentant 1:' : 'Representative 1:'}</div>
                                                <div className="value">{reg.representative_1.first_name} {reg.representative_1.last_name}</div>
                                                {reg.representative_1.email && <div style={{ fontSize: '13px', color: '#666' }}>{reg.representative_1.email}</div>}
                                                {reg.representative_1.phone && <div style={{ fontSize: '13px', color: '#666' }}>{reg.representative_1.phone}</div>}
                                            </DetailItem>
                                        )}

                                        {reg.representative_2?.first_name && (
                                            <DetailItem>
                                                <div className="label">{fr ? 'Représentant 2:' : 'Representative 2:'}</div>
                                                <div className="value">{reg.representative_2.first_name} {reg.representative_2.last_name}</div>
                                                {reg.representative_2.email && <div style={{ fontSize: '13px', color: '#666' }}>{reg.representative_2.email}</div>}
                                            </DetailItem>
                                        )}

                                        {reg.company_name && (
                                            <DetailItem>
                                                <div className="label">{fr ? 'Société:' : 'Company:'}</div>
                                                <div className="value">{reg.company_name}</div>
                                            </DetailItem>
                                        )}

                                        {reg.created_at && (
                                            <DetailItem>
                                                <div className="label">{fr ? 'Date d\'inscription:' : 'Registration date:'}</div>
                                                <div className="value">{dayjs(reg.created_at).format('DD/MM/YYYY')}</div>
                                            </DetailItem>
                                        )}

                                        <div style={{ marginTop: '15px' }}>
                                            <BlueButton onClick={() => navigate(isPoplA ? `/evenements/sdo/register?eid=${reg.event_id}&pop=a` : `/evenements/sdo/candidatures?eid=${reg.event_id}`)}>
                                                <img src="/img/fleche_blanche.png" alt="" />
                                                {fr ? 'Gérer ma candidature' : 'Manage my application'}
                                            </BlueButton>
                                        </div>
                                    </ContactInfoColumn>

                                    <ProjectsColumn>
                                        {isPoplA ? (
                                            <>
                                                <BigH3 style={{ marginBottom: '15px' }}>
                                                    {fr ? 'Projets' : 'Projects'} ({reg.projects?.length || 0})
                                                </BigH3>
                                                {(reg.projects || []).map((project, idx) => (
                                                    <div key={project.id || idx} style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '8px', padding: '12px 16px', marginBottom: '8px' }}>
                                                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>{fr ? `Projet ${idx + 1}` : `Project ${idx + 1}`}</div>
                                                        {project.description && <div style={{ fontSize: '14px', color: '#444', marginBottom: '4px' }}>{project.description.substring(0, 150)}{project.description.length > 150 ? '...' : ''}</div>}
                                                        {project.typologie && <div style={{ fontSize: '13px', color: '#666' }}>{fr ? 'Typologie:' : 'Typology:'} {project.typologie}</div>}
                                                        {project.profil_createur?.length > 0 && (
                                                            <div style={{ fontSize: '13px', color: '#666' }}>
                                                                {fr ? 'Profil:' : 'Profile:'} {project.profil_createur.map(v => PROFIL_CREATEUR_OPTIONS.find(o => o.value === v)?.label_fr || v).join(', ')}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </>
                                        ) : (
                                            <>
                                                <BigH3 style={{ marginBottom: '15px' }}>
                                                    {fr ? 'Portfolio' : 'Portfolio'} ({reg.portfolio_projects?.length || 0})
                                                </BigH3>
                                                {(reg.portfolio_projects || []).map((project, idx) => (
                                                    <div key={project.id || idx} style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '8px', padding: '12px 16px', marginBottom: '8px' }}>
                                                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>{project.title || (fr ? `Projet ${idx + 1}` : `Project ${idx + 1}`)}</div>
                                                        {project.description && <div style={{ fontSize: '14px', color: '#444', marginBottom: '4px' }}>{project.description.substring(0, 150)}{project.description.length > 150 ? '...' : ''}</div>}
                                                        {project.type && <div style={{ fontSize: '13px', color: '#666' }}>{fr ? 'Type:' : 'Type:'} {project.type}</div>}
                                                        {project.confidentiality === 'confidentiel' && <div style={{ fontSize: '12px', color: '#e65100', fontWeight: 500, marginTop: '4px' }}>{fr ? 'Confidentiel' : 'Confidential'}</div>}
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </ProjectsColumn>
                                </CandidatureContentLayout>
                            </CandidatureContainer>
                        );
                    })}
                </>
            )}

            {/* Milan 2026 Submissions */}
            {milanSubmissions.length > 0 && (
                <>
                    {milanSubmissions.map((submission) => {
                        const mediaBaseUrl = process.env.REACT_APP_MEDIA_BASE_URL;
                        return (
                            <CandidatureContainer key={submission._id}>
                                <CandidatureHeader>
                                    <div className="title-badge">
                                        <EventBadge>
                                            Milan 2026
                                        </EventBadge>
                                    </div>
                                </CandidatureHeader>

                                <CandidatureContentLayout>
                                    <ContactInfoColumn>
                                        <BigH3 style={{marginBottom:"15px"}}>
                                            {i18n.language === 'fr' ? 'Informations de contact' : 'Contact Information'}
                                        </BigH3>

                                        <DetailItem>
                                            <div className="label">{i18n.language === 'fr' ? 'Nom:' : 'Last name:'}</div>
                                            <div className="value">{submission.data.lastName}</div>
                                        </DetailItem>

                                        <DetailItem>
                                            <div className="label">{i18n.language === 'fr' ? 'Prénom:' : 'First name:'}</div>
                                            <div className="value">{submission.data.firstName}</div>
                                        </DetailItem>

                                        <DetailItem>
                                            <div className="label">{i18n.language === 'fr' ? 'Email:' : 'Email:'}</div>
                                            <div className="value">{submission.data.email}</div>
                                        </DetailItem>

                                        <DetailItem>
                                            <div className="label">{i18n.language === 'fr' ? 'Téléphone:' : 'Phone:'}</div>
                                            <div className="value">{submission.data.phoneNumber}</div>
                                        </DetailItem>

                                        <DetailItem>
                                            <div className="label">{i18n.language === 'fr' ? 'Date de soumission:' : 'Submission date:'}</div>
                                            <div className="value">{formatDateIfExists(submission.createdAt)}</div>
                                        </DetailItem>

                                        <div style={{marginTop: '20px'}}>
                                            <BlueButton onClick={() => navigate(`/mycandidatures/milan2026/${submission._id}`)}>
                                                <img src="/img/fleche_blanche.png" alt=""/>
                                                {i18n.language === 'fr' ? 'Voir toutes les infos' : 'View all details'}
                                            </BlueButton>
                                        </div>
                                    </ContactInfoColumn>

                                    <ProjectsColumn>
                                        <BigH3 style={{marginBottom:"15px"}}>
                                            {i18n.language === 'fr' ? 'Pièces proposées' : 'Proposed Pieces'} ({submission.data.pieces?.length || 0})
                                        </BigH3>

                                        {submission.data.pieces?.map((piece, index) => (
                                            <MilanPieceCard key={index}>
                                                <MilanPieceTitle>
                                                    {i18n.language === 'fr' ? `Pièce ${index + 1}: ` : `Piece ${index + 1}: `}
                                                    {piece.name}
                                                </MilanPieceTitle>

                                                <MilanDataGrid>
                                                    <MilanDataLabel>{i18n.language === 'fr' ? 'Typologie:' : 'Typology:'}</MilanDataLabel>
                                                    <MilanDataValue>
                                                        {piece.typologie === 'autre' && piece.typologieOther
                                                            ? piece.typologieOther
                                                            : getTypologyLabel(piece.typologie)}
                                                    </MilanDataValue>

                                                    <MilanDataLabel>{i18n.language === 'fr' ? 'Date de création:' : 'Creation date:'}</MilanDataLabel>
                                                    <MilanDataValue>{piece.creationDate}</MilanDataValue>

                                                    <MilanDataLabel>{i18n.language === 'fr' ? 'Dimensions:' : 'Dimensions:'}</MilanDataLabel>
                                                    <MilanDataValue>{piece.dimensions}</MilanDataValue>

                                                    <MilanDataLabel>{i18n.language === 'fr' ? 'Matériaux:' : 'Materials:'}</MilanDataLabel>
                                                    <MilanDataValue>{piece.materials}</MilanDataValue>
                                                </MilanDataGrid>

                                                {piece.uploadedMedia && piece.uploadedMedia.length > 0 && (
                                                    <MilanMediaSection>
                                                        <strong>
                                                            {i18n.language === 'fr' ? 'Médias uploadés' : 'Uploaded Media'} ({piece.uploadedMedia.length})
                                                        </strong>
                                                        <MilanMediaGrid>
                                                            {piece.uploadedMedia.map((media, idx) => (
                                                                <MilanMediaCard
                                                                    key={idx}
                                                                    onClick={() => window.open(`${mediaBaseUrl}/${media.path}`, '_blank')}
                                                                >
                                                                    <MilanMediaPreview>
                                                                        {media.mimetype.startsWith('image/') ? (
                                                                            <img
                                                                                src={`${mediaBaseUrl}/${media.path}`}
                                                                                alt={media.originalname}
                                                                            />
                                                                        ) : media.mimetype.startsWith('video/') ? (
                                                                            <div>🎥 {i18n.language === 'fr' ? 'Vidéo' : 'Video'}</div>
                                                                        ) : (
                                                                            <div>📄 PDF</div>
                                                                        )}
                                                                    </MilanMediaPreview>
                                                                    <MilanMediaInfo>
                                                                        <div className="filename">{media.originalname}</div>
                                                                    </MilanMediaInfo>
                                                                </MilanMediaCard>
                                                            ))}
                                                        </MilanMediaGrid>
                                                    </MilanMediaSection>
                                                )}
                                            </MilanPieceCard>
                                        ))}
                                    </ProjectsColumn>
                                </CandidatureContentLayout>
                            </CandidatureContainer>
                        );
                    })}
                </>
            )}
        </Section>
    );
};

export default MyCandidatures;